// Projects (sprint 7 §7.6): the original card grid for every role (owner decision 6). Users see active
// projects with their own time (read-only); Admins see team totals, a ⋯ menu and the Archived section.
// Sprint 12 (§9.1): every project is members-only. Anyone with no loggable project sees "You're not on any
// projects yet." (Admins above their Admin grid), and the Admin card shows "N members".
import { ChevronDown, Folder, MoreHorizontal, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ColorSwatch, projectBorderStyle } from '@/components/ColorSwatch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { useJoinAnyProject } from '@/hooks/use-project-members';
import { useAdminProjects, useProjects, useProjectStatusAction } from '@/hooks/use-projects';
import type { ProjectConfig } from '@/types/settings';
import { cn } from '@/lib/cn';
import { formatDateTime, formatDurationHm } from '@/lib/format';
import type { AdminProject } from '@/types/projects';
import { ProjectFormDialog } from './ProjectFormDialog';

const LOADING_CARDS = 6;
const PAGE_HINT = 'Projects group your time entries. An archived project keeps its data.';

function CardGridSkeleton(): JSX.Element {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: LOADING_CARDS }, (_, i) => (
        <li key={i}>
          <Skeleton className="h-28 rounded-xl" />
        </li>
      ))}
    </ul>
  );
}

interface ProjectActionsMenuProps {
  name: string;
  onEdit: () => void;
  /** Omitted for archived projects (Edit only). */
  onArchive?: () => void;
  /** Sprint 11 (Decision 3): only when the Admin is not an effective member. */
  onJoin?: () => void;
}

/** Admin ⋯ menu. Non-modal (sprint-06 learning: a modal menu opening a Dialog can leave pointer-events: none). */
function ProjectActionsMenu({ name, onEdit, onArchive, onJoin }: ProjectActionsMenuProps): JSX.Element {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${name}`} onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        {onJoin && <DropdownMenuItem onSelect={onJoin}>Join as Member</DropdownMenuItem>}
        {onArchive && <DropdownMenuItem onSelect={onArchive}>Archive</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ProjectCardProps {
  id: string;
  name: string;
  color: string;
  metricLabel: string;
  seconds: number;
  menu?: JSX.Element;
  /** Sprint 11: the Admin membership line. */
  config?: ProjectConfig;
}

function ProjectCard({ id, name, color, metricLabel, seconds, menu, config }: ProjectCardProps): JSX.Element {
  const navigate = useNavigate();
  return (
    <li>
      {/* The whole card navigates (mouse); the name link is the keyboard/AT path. */}
      <div className="card relative h-full cursor-pointer transition-colors hover:bg-accent/40" style={projectBorderStyle(color)} onClick={() => navigate(`/projects/${id}`)}>
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/projects/${id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex min-w-0 items-center gap-2 rounded text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ColorSwatch hex={color} size={10} />
            <span className="truncate">{name}</span>
          </Link>
          {menu}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{metricLabel}</p>
        <p className="font-mono text-lg tabular-nums text-foreground">{formatDurationHm(seconds)}</p>
        {config && <MembershipLine config={config} />}
      </div>
    </li>
  );
}

const ROLE_LABEL = { member: 'Member', supervisor: 'Supervisor' } as const;

export const NOT_ON_PROJECTS_TITLE = "You're not on any projects yet.";
export const NOT_ON_PROJECTS_HINT = 'Ask an Admin or your Supervisor to add you.';

function membershipBadge(config: ProjectConfig): string {
  return `${config.memberCount} ${config.memberCount === 1 ? 'member' : 'members'}`;
}

function NotOnProjects({ className }: { className?: string }): JSX.Element {
  return <EmptyState variant="card" className={className} icon={Folder} title={NOT_ON_PROJECTS_TITLE} hint={NOT_ON_PROJECTS_HINT} />;
}

function MembershipLine({ config }: { config: ProjectConfig }): JSX.Element {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <Badge>{membershipBadge(config)}</Badge>
      <span>You: {config.myRole ? ROLE_LABEL[config.myRole] : '—'}</span>
    </div>
  );
}

function UserProjects(): JSX.Element {
  const { data: projects, isLoading } = useProjects();
  if (isLoading) return <CardGridSkeleton />;
  if (!projects || projects.length === 0) {
    return <NotOnProjects className="py-16" />;
  }
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((p) => (
        <ProjectCard key={p.id} id={p.id} name={p.name} color={p.color} metricLabel="Your time" seconds={p.ownSeconds} />
      ))}
    </ul>
  );
}

interface AdminProjectsProps {
  editing: AdminProject | null | 'new';
  setEditing: (value: AdminProject | null | 'new') => void;
}

function AdminProjects({ editing, setEditing }: AdminProjectsProps): JSX.Element {
  const [showArchived, setShowArchived] = useState(false);
  const active = useAdminProjects('active');
  const archived = useAdminProjects('archived', showArchived);
  const statusAction = useProjectStatusAction();
  const join = useJoinAnyProject();
  const mine = useProjects();
  const [archiving, setArchiving] = useState<AdminProject | null>(null);
  const activeColors = useMemo(() => (active.data ?? []).map((p) => p.color), [active.data]);

  function restore(project: AdminProject): void {
    statusAction.mutate({ id: project.id, action: 'restore' }, { onSuccess: () => toast.success(`${project.name} restored.`) });
  }

  const restoringId = statusAction.isPending && statusAction.variables?.action === 'restore' ? statusAction.variables.id : null;

  return (
    <>
      {/* Sprint 12: an Admin is bound by the members-only rule too; the Admin grid stays below. */}
      {mine.data && mine.data.length === 0 && <NotOnProjects className="mb-6" />}
      {active.isLoading ? (
        <CardGridSkeleton />
      ) : !active.data || active.data.length === 0 ? (
        <EmptyState variant="card" className="py-16" icon={Folder} title="No projects yet" hint="Create a project to start tracking time" />
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {active.data.map((p) => (
            <ProjectCard
              key={p.id}
              id={p.id}
              name={p.name}
              color={p.color}
              metricLabel="Team total"
              seconds={p.teamSeconds}
              config={p.config}
              menu={
                <ProjectActionsMenu
                  name={p.name}
                  onEdit={() => setEditing(p)}
                  onArchive={() => setArchiving(p)}
                  onJoin={p.config?.myRole === null ? () => join.mutate(p.id, { onSuccess: () => toast.success(`You joined ${p.name}.`) }) : undefined}
                />
              }
            />
          ))}
        </ul>
      )}

      <section className="mt-8">
        <Button variant="ghost" aria-expanded={showArchived} onClick={() => setShowArchived((v) => !v)}>
          <ChevronDown className={cn('transition-transform duration-150', showArchived && 'rotate-180')} aria-hidden="true" />
          Archived projects{archived.data ? ` (${archived.data.length})` : ''}
        </Button>
        {showArchived &&
          (archived.isLoading ? (
            <Skeleton className="mt-3 h-24 w-full" />
          ) : !archived.data || archived.data.length === 0 ? (
            <EmptyState variant="inset" title="No archived projects." />
          ) : (
            <ul className="mt-3 space-y-2 animate-page-in">
              {archived.data.map((p) => (
                <li key={p.id} className="card flex flex-wrap items-center gap-3">
                  {/* Only the dot is faded; the text stays AA-readable (A-5). */}
                  <ColorSwatch hex={p.color} size={10} className="opacity-60" />
                  <Link to={`/projects/${p.id}`} className="min-w-0 flex-1 truncate text-sm text-muted-foreground hover:underline">
                    {p.name}
                  </Link>
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatDurationHm(p.teamSeconds)}</span>
                  {p.archivedAt && <span className="text-xs text-muted-foreground">Archived {formatDateTime(p.archivedAt)}</span>}
                  <Button variant="ghost" size="xs" onClick={() => restore(p)} disabled={restoringId === p.id}>
                    {restoringId === p.id ? 'Restoring…' : 'Restore'}
                  </Button>
                  <ProjectActionsMenu name={p.name} onEdit={() => setEditing(p)} />
                </li>
              ))}
            </ul>
          ))}
      </section>

      <ProjectFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        project={editing === 'new' ? null : editing}
        activeColors={activeColors}
      />
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(o) => !o && setArchiving(null)}
        title={`Archive ${archiving?.name ?? ''}?`}
        body="It disappears from pickers for new time. Existing time keeps it and it stays in reports."
        confirmLabel="Archive"
        pending={statusAction.isPending}
        onConfirm={() => {
          if (!archiving) return;
          statusAction.mutate(
            { id: archiving.id, action: 'archive' },
            {
              onSuccess: () => {
                toast.success(`${archiving.name} archived.`);
                setArchiving(null);
              },
              onError: () => setArchiving(null),
            },
          );
        }}
      />
    </>
  );
}

/** Admin-only controls are not rendered for Users; the role comes from /api/me (§10.0). */
export function ProjectsPage(): JSX.Element {
  const { data: me, isLoading } = useMe();
  const [editing, setEditing] = useState<AdminProject | null | 'new'>(null);
  const isAdmin = me?.role === 'admin';
  return (
    <Page width="4xl">
      <PageHeader
        title="Projects"
        hint={PAGE_HINT}
        actions={
          isAdmin && (
            <Button onClick={() => setEditing('new')}>
              <Plus className="[stroke-width:2]" /> New project
            </Button>
          )
        }
      />
      {isLoading || !me ? <CardGridSkeleton /> : isAdmin ? <AdminProjects editing={editing} setEditing={setEditing} /> : <UserProjects />}
    </Page>
  );
}
